/**
 * DialogSelect — a reusable custom-popover select modeled on IndustryPicker
 * (the Add-New-Client industry dropdown), generic over its options. Replaces
 * native <select> elements inside form dialogs so every control shares the
 * §9 dialog-input look: h-11 trigger, 0.5px border, rounded-lg, text-sm,
 * shadow-xs, a motion/AnimatePresence popover, an optional MagnifyingGlass
 * search box, and a Check on the selected row.
 *
 * Visual + interaction parity with IndustryPicker:
 *   - trigger: left span (value text-foreground / placeholder
 *     text-muted-foreground, optional leading Phosphor icon) + trailing CaretDown
 *   - panel: motion dropdown, optional search Input, option rows styled with the
 *     hover border/shadow affordance, Check on the selected row
 *   - closes on outside-click + Escape, keyboard accessible (role=listbox/option)
 *
 * Search visibility follows the SelectField convention: shown when
 * `searchable` is set, else auto (options.length > 8) — long metric/client
 * lists get search, the 3-option enums (goal type, unit) don't.
 *
 * IndustryPicker itself is intentionally left UNCHANGED (it carries the
 * industry-keyword icon inference); it can adopt this later.
 */

import { useState, useRef, useEffect, useMemo } from "react";
import { Input } from "@provenleap/ui";
import { MagnifyingGlass, Check, CaretDown } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "motion/react";
import type { Icon } from "@phosphor-icons/react";

export interface DialogSelectOption {
  value: string;
  label: string;
  /** Optional leading Phosphor icon, rendered on the trigger + the row. */
  icon?: Icon;
  /**
   * Optional leading image URL (e.g. a platform logo) — rendered on the trigger
   * + the row like `icon`, but for raster/SVG logos rather than Phosphor icons.
   */
  iconUrl?: string;
  /** When true the row is shown but not selectable. */
  disabled?: boolean;
}

export interface DialogSelectProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  options: DialogSelectOption[];
  placeholder?: string;
  /** Disables the trigger entirely (e.g. the client selector while editing). */
  disabled?: boolean;
  /**
   * Force-show (true) / force-hide (false) the search box. Default: auto —
   * shown only when options.length > 8 (the SelectField convention).
   */
  searchable?: boolean;
  /** Accessible name when no associated <Label htmlFor> is present. */
  ariaLabel?: string;
  searchPlaceholder?: string;
  /**
   * Popover direction. Default "down" (panel beneath the trigger). Use "up"
   * inside bottom-anchored sheets where a downward panel would overflow.
   */
  direction?: "up" | "down";
}

export function DialogSelect({
  id,
  value,
  onChange,
  options,
  placeholder = "Select…",
  disabled = false,
  searchable,
  ariaLabel,
  searchPlaceholder = "Search…",
  direction = "down",
}: DialogSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const popoverRef = useRef<HTMLDivElement>(null);

  const showSearch = searchable ?? options.length > 8;

  useEffect(() => {
    if (!isOpen) return;
    function handleClickOutside(e: MouseEvent) {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearch("");
      }
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setIsOpen(false);
        setSearch("");
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKey);
    };
  }, [isOpen]);

  const filtered = useMemo(() => {
    if (!showSearch || !search) return options;
    const q = search.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, search, showSearch]);

  const selectedOption = options.find((o) => o.value === value);
  const SelectedIcon = selectedOption?.icon;

  function handleSelect(option: DialogSelectOption) {
    if (option.disabled) return;
    onChange(option.value);
    setIsOpen(false);
    setSearch("");
  }

  const popoverPosition =
    direction === "down"
      ? "absolute left-0 top-full mt-2.5 z-[60] w-full rounded-xl bg-popover shadow-md"
      : "absolute left-0 bottom-full mb-2.5 z-[60] w-full rounded-xl bg-popover shadow-md";

  const arrowPosition =
    direction === "down"
      ? "absolute -top-[5px] left-1/2 -translate-x-1/2 w-2.5 h-2.5 bg-popover rotate-45 z-10"
      : "absolute -bottom-[5px] left-1/2 -translate-x-1/2 w-2.5 h-2.5 bg-popover rotate-45 z-10";

  const arrowBorderStyle =
    direction === "down"
      ? {
          borderLeft: "0.5px solid var(--hairline)",
          borderTop: "0.5px solid var(--hairline)",
        }
      : {
          borderRight: "0.5px solid var(--hairline)",
          borderBottom: "0.5px solid var(--hairline)",
        };

  return (
    <div className="relative" ref={popoverRef}>
      <button
        type="button"
        id={id}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        disabled={disabled}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className="flex h-11 w-full items-center justify-between rounded-lg border-[0.5px] border-input bg-background px-3 py-2 text-sm shadow-xs cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
      >
        <span
          className={`flex items-center gap-2 ${
            selectedOption ? "text-foreground" : "text-muted-foreground"
          }`}
        >
          {SelectedIcon && <SelectedIcon size={16} className="shrink-0 text-foreground" />}
          {selectedOption?.iconUrl && (
            <img
              src={selectedOption.iconUrl}
              alt=""
              className="h-4 w-4 shrink-0 rounded-sm object-contain"
              loading="lazy"
            />
          )}
          {selectedOption?.label ?? placeholder}
        </span>
        <CaretDown size={16} className="opacity-50" />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            role="listbox"
            initial={{ opacity: 0, scale: 0.95, y: direction === "down" ? -4 : 4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: direction === "down" ? -4 : 4 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
            className={popoverPosition}
            style={{ border: "0.5px solid var(--hairline)" }}
          >
            <div className={arrowPosition} style={arrowBorderStyle} />
            {showSearch && (
              <div className="px-2 pt-2 pb-0.5">
                <div className="relative">
                  <MagnifyingGlass
                    size={14}
                    className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground"
                  />
                  <Input
                    placeholder={searchPlaceholder}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-7 h-10 text-sm border-[0.5px] border-input shadow-none focus-visible:ring-0 focus-visible:outline-none focus:outline-none rounded-lg"
                  />
                </div>
              </div>
            )}

            <div className="max-h-72 overflow-y-auto p-2">
              {filtered.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-3">
                  No options found
                </p>
              )}
              {filtered.map((option) => {
                const OptionIcon = option.icon;
                const isSelected = value === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    aria-disabled={option.disabled || undefined}
                    onClick={() => handleSelect(option)}
                    className={`w-full flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm border-[0.9px] border-transparent hover:bg-muted/20 hover:border-input hover:shadow-xs cursor-pointer ${
                      option.disabled ? "opacity-40 cursor-not-allowed" : ""
                    }`}
                  >
                    {OptionIcon && (
                      <OptionIcon size={16} className="shrink-0 text-foreground" />
                    )}
                    {option.iconUrl && (
                      <img
                        src={option.iconUrl}
                        alt=""
                        className="h-4 w-4 shrink-0 rounded-sm object-contain"
                        loading="lazy"
                      />
                    )}
                    <span className="flex-1 text-left">{option.label}</span>
                    {isSelected && <Check size={14} className="text-primary shrink-0" />}
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
